import type {
    CopyToClipboardPayload,
    ExportTextPayload,
    ReadinessChangedPayload,
    SetFullscreenPayload,
    SetFullscreenResult,
    SharePayload,
    ThemeChangedPayload,
    UIRemoteHandlers,
    UiVisibilityChangedPayload,
} from '@publicala/delfino';
import { hostBridge } from '@publicala/delfino';
import { isMethodNotImplementedError } from '../bridgeErrors';
import { isE2EMockActive } from '../e2eMockGuard';

type AnnouncementMethod = 'themeChanged' | 'uiVisibilityChanged' | 'readinessChanged';

/**
 * UI API for Volpe.
 * Provides methods for UI actions via hostBridge.
 */
class UIAPI {
    private inFlightShare: Promise<void> | null = null;

    /**
     * Close the reader.
     * Skipped in E2E standalone mode to prevent errors when hostBridge is not initialized.
     */
    async close(): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }
        return hostBridge.ui.close();
    }

    /**
     * Open the login form.
     * Skipped in E2E standalone mode to prevent errors when hostBridge is not initialized.
     */
    async openLoginForm(): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }
        return hostBridge.ui.openLoginForm();
    }

    /**
     * Toggle payment links modal.
     * Skipped in E2E standalone mode to prevent errors when hostBridge is not initialized.
     */
    async togglePaymentLinks(): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }
        return hostBridge.ui.togglePaymentLinks();
    }

    /**
     * Share content via the host's share mechanism.
     * Skipped in E2E standalone mode to prevent errors when hostBridge is not initialized.
     * Concurrent calls are ignored while a previous share is still pending — the
     * native Web Share API only allows one in-flight share at a time, and a second
     * call would throw InvalidStateError in the host.
     */
    async shareContent(payload: SharePayload): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }
        if (this.inFlightShare) {
            return this.inFlightShare;
        }
        this.inFlightShare = hostBridge.ui.share(payload).finally(() => {
            this.inFlightShare = null;
        });
        return this.inFlightShare;
    }

    /**
     * Copy text to clipboard.
     * Skipped in E2E standalone mode to prevent errors when hostBridge is not initialized.
     */
    async copyToClipboard(payload: CopyToClipboardPayload): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }
        return hostBridge.ui.copyToClipboard(payload);
    }

    /**
     * Ask the host to enter or exit fullscreen. Volpe runs in an iframe, so only the
     * host can apply fullscreen. It answers with the state actually in effect.
     * Skipped in E2E standalone mode to prevent errors when hostBridge is not initialized.
     * @param payload - Desired fullscreen state
     * @returns The fullscreen state in effect after the host applied the request, or null
     * when the host predates the fullscreen contract. That version skew is expected during
     * rollout, so it stays below the error bar; every other failure keeps propagating.
     */
    async setFullscreen(payload: SetFullscreenPayload): Promise<SetFullscreenResult | null> {
        if (isE2EMockActive()) {
            return { isFullscreen: payload.isFullscreen };
        }
        try {
            return await hostBridge.ui.setFullscreen(payload);
        } catch (error) {
            if (isMethodNotImplementedError(error)) {
                console.debug('[UIAPI] host does not implement the fullscreen request:', error);
                return null;
            }
            throw error;
        }
    }

    /**
     * Announce the reader theme in effect and its resolved chrome color, so
     * the host can paint the system chrome around the reader with it (status
     * bar, safe-area bands, browser theme color).
     */
    themeChanged(payload: ThemeChangedPayload): Promise<void> {
        return this.announce('themeChanged', payload);
    }

    /**
     * Announce whether the reader UI (toolbar, footer) is visible, so hosts
     * can show and hide the system bars in sync with it.
     */
    uiVisibilityChanged(payload: UiVisibilityChangedPayload): Promise<void> {
        return this.announce('uiVisibilityChanged', payload);
    }

    /** Announce initial rendering state independently of toolbar visibility. */
    readinessChanged(payload: ReadinessChangedPayload): Promise<void> {
        return this.announce('readinessChanged', payload);
    }

    /**
     * Fire-and-forget a cosmetic announcement to the host. Painting chrome
     * must never disturb reading, so every failure is swallowed.
     * Skipped in E2E standalone mode to prevent errors when hostBridge is not initialized.
     */
    private async announce<M extends AnnouncementMethod>(
        method: M,
        payload: Parameters<UIRemoteHandlers[M]>[0]
    ): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }
        try {
            if (hostBridge.remoteImplements('ui', method) === false) {
                return;
            }
            const send = (hostBridge.ui as UIRemoteHandlers)[method] as (payload: unknown) => Promise<void>;
            await send(payload);
        } catch (error) {
            console.debug(`[UIAPI] host could not receive ui.${method}:`, error);
        }
    }

    /**
     * Export text to a file.
     * Skipped in E2E standalone mode to prevent errors when hostBridge is not initialized.
     * @param payload - Contains text and fileName
     */
    async exportText(payload: ExportTextPayload): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }
        return hostBridge.ui.exportText(payload);
    }
}

export const uiAPI = new UIAPI();
